import React, { useState } from "react";
import type { AnalyzePairResponse } from "../api/myTypes";
import LLMDialogLog from "./LLMDialogLog";
import { trackRecommendation, trackPosition } from "../api/etfApi";

interface PairAnalysisResultProps {
  data: AnalyzePairResponse;
  dialogButton?: React.ReactNode;
}

const PairAnalysisResult: React.FC<PairAnalysisResultProps> = ({ data, dialogButton }) => {
  // Use state to ensure component re-renders when data changes
  const [rotationData, setRotationData] = React.useState(data?.rotation_recommendation || {});
  const marketRegime = data.market_regime;
  const [trackingStatus, setTrackingStatus] = useState<{ type: "recommendation" | "position" | null; message: string }>({ type: null, message: "" });
  
  // Update state when data changes
  React.useEffect(() => {
    const rotation = data?.rotation_recommendation;
    if (rotation) {
      // Always update to ensure we get the latest data
      setRotationData({ ...rotation }); // Create new object to ensure state update
      console.log('PairAnalysisResult - Updated rotation_recommendation:', rotation);
      console.log('PairAnalysisResult - text_reasoning:', rotation.text_reasoning);
      console.log('PairAnalysisResult - text_reasoning exists:', !!rotation.text_reasoning);
    } else {
      // Reset if rotation_recommendation is not available
      setRotationData({});
    }
  }, [data]);

  // Get regime badge color
  const getRegimeBadgeColor = (regimeType: string) => {
    if (regimeType?.includes("Bull")) return "#10b981"; // green
    if (regimeType?.includes("Bear")) return "#ef4444"; // red
    if (regimeType?.includes("Choppy") || regimeType?.includes("Sideways")) return "#f59e0b"; // amber
    if (regimeType?.includes("High Volatility")) return "#8b5cf6"; // purple
    if (regimeType?.includes("Low Volatility")) return "#3b82f6"; // blue
    return "#6b7280"; // gray
  };
  
  const {
    conviction,
    entry_zone,
    exit_zone,
    position_size,
    stop_loss,
    take_profit,
    text_reasoning,
    signals_summary,
    primary_horizon,
    secondary_horizon,
    horizon, // Fallback if primary_horizon/secondary_horizon not available
    rotate_signal,
  } = rotationData;
  
  // Use rotate_signal as rating if available
  const rating = rotate_signal;
  
  // Check if text_reasoning exists and is valid
  const hasTextReasoning = text_reasoning && 
                           typeof text_reasoning === 'string' && 
                           text_reasoning.trim().length > 0;

  const handleTrackRecommendation = async () => {
    try {
      const recommendationId = `rec_${data.primary_ticker}_${data.secondary_ticker}_${Date.now()}`;
      
      // Extract entry price from entry_zone if possible
      const entryPriceMatch = entry_zone?.match(/\$?([\d.]+)/);
      const entryPrice = entryPriceMatch ? parseFloat(entryPriceMatch[1]) : undefined;
      
      await trackRecommendation({
        recommendationId,
        ticker: `${data.primary_ticker}/${data.secondary_ticker}`,
        strategy: "pair_rotation",
        model: data.analysis_model || "classic",
        entryPrice: entryPrice,
        recommendationData: rotationData,
        marketRegime: marketRegime || undefined,
      });
      setTrackingStatus({ type: "recommendation", message: "✓ Recommendation tracked successfully!" });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
      // Dispatch event to refresh Performance tab
      window.dispatchEvent(new CustomEvent('refresh-performance'));
    } catch (error: any) {
      setTrackingStatus({ type: "recommendation", message: `Error: ${error.message}` });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
    }
  };

  // Helper function to extract price from various formats
  // Returns the average if it's a range, otherwise the single value
  const extractPrice = (priceStr: string | null | undefined, useAverage: boolean = true): number | undefined => {
    if (!priceStr) return undefined;
    
    // Try to extract number from various formats:
    // "$100", "100", "$100-105", "100-105", "$100.50", "100.50", "around $100", etc.
    
    // First, try to match a range pattern
    const rangePattern = /\$?\s*([\d,]+\.?\d*)\s*-\s*\$?\s*([\d,]+\.?\d*)/;
    const rangeMatch = priceStr.match(rangePattern);
    
    if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
      // It's a range - calculate average if useAverage is true, otherwise use lower value
      const lower = parseFloat(rangeMatch[1].replace(/,/g, ''));
      const upper = parseFloat(rangeMatch[2].replace(/,/g, ''));
      
      if (!isNaN(lower) && !isNaN(upper) && lower > 0 && upper > 0 && upper >= lower) {
        return useAverage ? (lower + upper) / 2 : lower;
      }
    }
    
    // If not a range, try single number pattern
    const singlePattern = /\$?\s*([\d,]+\.?\d*)/;
    const singleMatch = priceStr.match(singlePattern);
    
    if (singleMatch && singleMatch[1]) {
      const priceValue = singleMatch[1].replace(/,/g, '');
      const parsed = parseFloat(priceValue);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    
    return undefined;
  };

  const handleTrackPosition = async () => {
    try {
      const positionId = `pos_${data.primary_ticker}_${Date.now()}`;
      const recommendationId = `rec_${data.primary_ticker}_${data.secondary_ticker}_${Date.now()}`;
      
      // Try multiple sources for entry price (use average for ranges)
      let entryPrice: number | undefined = undefined;
      
      // 1. Try entry_zone field (PRIORITY - use average if range)
      if (entry_zone) {
        entryPrice = extractPrice(entry_zone, true); // true = use average for ranges
      }
      
      // 2. Try current price from asset profile as fallback
      if (!entryPrice && data.pair_profile?.primary_profile?.price_context?.candles?.length > 0) {
        const candles = data.pair_profile.primary_profile.price_context.candles;
        const latestCandle = candles[candles.length - 1];
        if (latestCandle?.close) {
          entryPrice = typeof latestCandle.close === 'number' 
            ? latestCandle.close 
            : parseFloat(String(latestCandle.close));
        }
      }
      
      // 3. Try extracting from text_reasoning if it contains price info
      if (!entryPrice && text_reasoning) {
        entryPrice = extractPrice(text_reasoning, true);
      }
      
      // Extract stop loss and take profit (use average for ranges)
      const stopLoss = extractPrice(stop_loss, true);
      const takeProfit = extractPrice(take_profit, true);

      if (!entryPrice || isNaN(entryPrice)) {
        setTrackingStatus({ 
          type: "position", 
          message: "Error: Could not extract entry price. Please ensure the recommendation includes an entry zone or price." 
        });
        setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
        return;
      }

      // Use primary ticker for position tracking
      await trackPosition({
        positionId,
        recommendationId,
        ticker: data.primary_ticker,
        entryPrice,
        stopLoss,
        takeProfit,
      });
      setTrackingStatus({ type: "position", message: "✓ Position tracked successfully!" });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
      // Dispatch event to refresh Positions tab
      window.dispatchEvent(new CustomEvent('refresh-positions'));
    } catch (error: any) {
      setTrackingStatus({ type: "position", message: `Error: ${error.message}` });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
    }
  };

  return (
    <div className="analysis-grid">
      {/* Market Regime Badge */}
      {marketRegime && (
        <div className="card" style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "bold", marginBottom: "0.75rem" }}>
            Market Regime Analysis
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <span
              style={{
                display: "inline-block",
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                backgroundColor: getRegimeBadgeColor(marketRegime.regime_type),
                color: "white",
                fontWeight: "bold",
                fontSize: "0.875rem"
              }}
            >
              {marketRegime.regime_type}
            </span>
            <span style={{ fontSize: "0.875rem", color: "#ffffff" }}>
              Confidence: {marketRegime.regime_confidence}
            </span>
          </div>
          <p style={{ fontSize: "0.875rem", color: "#ffffff", marginBottom: "0.5rem", lineHeight: "1.5" }}>
            {marketRegime.regime_description}
          </p>
          {marketRegime.strategy_adjustments && (
            <div style={{ fontSize: "0.8rem", color: "#ffffff", fontStyle: "italic" }}>
              <strong>Strategy Adjustment:</strong> {marketRegime.strategy_adjustments}
            </div>
          )}
        </div>
      )}

      <div className="card main-rec-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h2 className="card-title">Rotation View</h2>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="primary-button"
              onClick={handleTrackRecommendation}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Track Trade
            </button>
            <button
              className="primary-button"
              onClick={handleTrackPosition}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Track Position
            </button>
          </div>
        </div>
        {trackingStatus.type && (
          <div style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: "6px",
            backgroundColor: trackingStatus.message.includes("Error") ? "#fee2e2" : "#d1fae5",
            color: trackingStatus.message.includes("Error") ? "#991b1b" : "#065f46",
            fontSize: "0.95rem",
            fontWeight: "500",
            border: `2px solid ${trackingStatus.message.includes("Error") ? "#fca5a5" : "#86efac"}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
            <span style={{ fontSize: "1.2rem" }}>
              {trackingStatus.message.includes("Error") ? "⚠️" : "✓"}
            </span>
            <span>{trackingStatus.message}</span>
          </div>
        )}
        <div className="rec-summary-row">
          {rating && (
            <span className="pill pill-rating">
              {rating.toUpperCase()}
            </span>
          )}
          {conviction && (
            <span className="pill pill-conviction">{conviction}</span>
          )}
          {(primary_horizon || secondary_horizon || horizon) && (
            <span className="pill pill-horizon">
              Horizon: {primary_horizon || secondary_horizon || horizon}
            </span>
          )}
        </div>
        {signals_summary && <p className="thesis-text">{signals_summary}</p>}
      </div>

      {/* AI Reasoning Section */}
      {hasTextReasoning && (
        <div className="card full-width">
          <section className="analysis-section">
            <h3>AI Reasoning</h3>
            <div className="section-body scrollable-reasoning">
              {text_reasoning}
            </div>
          </section>
        </div>
      )}

      {(entry_zone || exit_zone || position_size || stop_loss || take_profit) && (
        <div className="card full-width">
          <section className="analysis-section">
            <h3>Trade Parameters</h3>
            <div className="trade-grid">
              {entry_zone && (
                <div>
                  <div className="trade-label">Entry Zone</div>
                  <div>{entry_zone}</div>
                </div>
              )}
              {exit_zone && (
                <div>
                  <div className="trade-label">Exit Zone</div>
                  <div>{exit_zone}</div>
                </div>
              )}
              {position_size && (
                <div>
                  <div className="trade-label">Position Size</div>
                  <div>{position_size}</div>
                </div>
              )}
              {stop_loss && (
                <div>
                  <div className="trade-label">Stop Loss</div>
                  <div>{stop_loss}</div>
                </div>
              )}
              {take_profit && (
                <div>
                  <div className="trade-label">Take Profit</div>
                  <div>{take_profit}</div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {/* LLM Dialog Log */}
      {data.ai_dialogue_log && (
        <>
          {dialogButton && (
            <div style={{ textAlign: "right", marginTop: "16px", marginBottom: "8px" }}>
              {dialogButton}
            </div>
          )}
          <div className="llm-dialog-wrapper">
            <LLMDialogLog dialogLog={data.ai_dialogue_log} />
          </div>
        </>
      )}

      {/* Footer with metadata */}
      <div className="card-footer-row" style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid rgba(148, 163, 184, 0.2)" }}>
        <span className="meta-muted" style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
          {data.data_provider ? `Data: ${data.data_provider}` : ''}
        </span>
      </div>
    </div>
  );
};

export default PairAnalysisResult;







